import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { RappiderButtonModule, RappiderAlertModule } from '@rappider/rappider-components';
import { ButtonType, ButtonSize, AlertTypes, HeadingType, IconType } from '@rappider/rappider-components/utils';
import { AIChatGeneratorService } from '../../../services/ai-generator.service';
import { EntityComponentStateService } from '../../../services/entity-component-state.service';
import { EntityVisualizationService } from '../../../services/entity-visualization.service';
import { ComponentGeneratorService } from 'libs/content-editor/src/lib/services/component-generator.service';
import { ComponentDefinition, ProjectChannelMessage, ProjectModelWithRelations } from '@rappider/rappider-sdk';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { CompiledComponentRendererComponent } from
  'libs/content-tree-renderer/src/lib/components/compiled-component-renderer/compiled-component-renderer.component';
import { EntityComponentResponse } from '../../../utils/entity-interfaces';
import * as ProjectModelActions from 'libs/project/src/lib/states/project-model-state/project-model.actions';
import { getProjectModelsWithFields } from '@rappider/shared';


@Component({
  selector: 'rappider-ai-entity-component-response-renderer',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzSpinModule,
    NzCardModule,
    NzIconModule,
    NzToolTipModule,
    RappiderButtonModule,
    RappiderAlertModule,
    CompiledComponentRendererComponent
  ],
  templateUrl: './ai-entity-component-response-renderer.component.html',
  styleUrls: ['./ai-entity-component-response-renderer.component.scss']
})
export class AiEntityComponentResponseRendererComponent implements OnInit, OnDestroy, OnChanges {

  @Input() responseData: Record<string, unknown> | null = null;
  @Input() message: unknown = null;
  @Input() showActions = true;
  @Input() projectModelId?: string;
  @Input() messageId?: string;
  @Input() componentDefinitionId?: string;
  @Input() allMessages?: ProjectChannelMessage[];

  @Output() save = new EventEmitter<EntityComponentResponse>();
  @Output() splitterToggleRequested = new EventEmitter<boolean>();
  @Output() revertRequested = new EventEmitter<{ messageId: string; draftId: string; stepId: string }>();

  readonly ButtonType = ButtonType;
  readonly ButtonSize = ButtonSize;
  readonly AlertTypes = AlertTypes;
  readonly HeadingType = HeadingType;
  readonly IconType = IconType;

  activeProjectId?: string;
  private destroy$ = new Subject<void>();
  isCompilingLoading = false;
  isBundleLoading = false;
  createEntityComponentLoading = false;
  isRetrying = false;
  infoMessage: string | null = null;
  isVersionSaved = false;
  componentVersion: number | null = null;
  isReverting = false;
  hasDisplayComponent = false;

  constructor(
    private aiChatGeneratorService: AIChatGeneratorService,
    private entityComponentStateService: EntityComponentStateService,
    private entityVisualizationService: EntityVisualizationService,
    private componentGeneratorService: ComponentGeneratorService,
    private store: Store<unknown>
  ) {
    // Constructor intentionally left empty
  }

  ngOnInit(): void {
    this.subscribeToActiveProject();
    this.subscribeToEntityComponentState();
    this.subscribeToDisplayComponentStatus();

    // Set loading state from response data
    if (this.responseData) {
      this.isCompilingLoading = (this.responseData as Record<string, unknown>).isLoading === true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['messageId'] && changes['messageId'].currentValue) {
      this.subscribeToEntityComponentState();
    }
    if (changes['projectModelId'] && changes['projectModelId'].currentValue) {
      this.subscribeToDisplayComponentStatus();
    }
  }

  /**
   * Subscribe to entity component state changes
   */
  private subscribeToEntityComponentState(): void {
    if (!this.messageId) {
      this.isCompilingLoading = true;
      this.isBundleLoading = false;
      this.isRetrying = false;
      this.infoMessage = null;
      return;
    }

    const currentState = this.entityComponentStateService.getCurrentComponentState(this.messageId);
    this.isCompilingLoading = currentState.isCompilingLoading;
    this.isBundleLoading = currentState.isBundleLoading;
    this.isRetrying = currentState.isRetrying;
    this.infoMessage = currentState.infoMessage;

    this.entityComponentStateService.getComponentState$(this.messageId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.isCompilingLoading = state.isCompilingLoading;
        this.isBundleLoading = state.isBundleLoading;
        this.isRetrying = state.isRetrying;
        this.infoMessage = state.infoMessage;
      });
  }

  /**
 * Subscribe to display component status for the project model
 */
  private subscribeToDisplayComponentStatus(): void {
    if (!this.projectModelId) {
      this.hasDisplayComponent = false;
      return;
    }

    this.store.select(getProjectModelsWithFields)
      .pipe(takeUntil(this.destroy$))
      .subscribe((projectModels: ProjectModelWithRelations[] | undefined) => {
        const projectModel = projectModels?.find(pm => pm.id === this.projectModelId);
        this.hasDisplayComponent = this.entityVisualizationService.hasDisplayComponent(projectModel || null);
      });
  }

  /**
   * Subscribe to active project changes and update component state
   */
  private subscribeToActiveProject(): void {
    const messageObj = this.message as Record<string, unknown>;
    this.activeProjectId = messageObj?.projectId as string;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  /**
   * Get component definition object with bundle information
   * @returns Component definition object with bundle URL and content
   */
  getComponentDefinitionObject(): Record<string, unknown> {
    const componentDef = this.responseData?.componentDefinition as Record<string, unknown> || {};
    const state = this.messageId ? this.entityComponentStateService.getCurrentComponentState(this.messageId) : null;

    const definitionObject: Record<string, unknown> = {
      bundleContent: state?.bundleContent || null,
      id: componentDef?.id || '',
      title: componentDef?.title || '',
      description: componentDef?.description || '',
      selector: componentDef?.selector || '',
      className: componentDef?.className || ''
    };

    if (this.componentVersion) {
      definitionObject.version = this.componentVersion;
    } else if (componentDef?.version) {
      definitionObject.version = componentDef.version;
    }

    return definitionObject;
  }

  /**
   * Get component object for rendering
   * If step ID exists, creates a step preview component ID to enable step-specific bundle loading
   * @returns Component object with inputs and step-aware component ID
   */
  getComponentObject(): Record<string, unknown> {
    const componentSample = this.responseData?.componentSample as Record<string, unknown> || {};
    const stepId = this.getStepId();
    const componentId = stepId ? `step_preview_${stepId}` : 'preview_component';

    return {
      id: componentId,
      inputs: componentSample?.inputs || {}
    };
  }

  /**
   * Get bundle content for component
   * @returns Bundle content string or empty string
   */
  getBundleContent(): string {
    if (!this.messageId) {
      return '';
    }
    const state = this.entityComponentStateService.getCurrentComponentState(this.messageId);
    return state.bundleContent || '';
  }

  /**
   * Get error message from component state
   * @returns Error message string or null
   */
  getError(): string | null {
    if (!this.messageId) {
      return null;
    }
    const state = this.entityComponentStateService.getCurrentComponentState(this.messageId);
    return state.error;
  }

  /**
   * Get info message from component state
   * @returns Info message string or null
   */
  getInfoMessage(): string | null {
    if (!this.messageId) {
      return null;
    }
    const state = this.entityComponentStateService.getCurrentComponentState(this.messageId);
    return state?.infoMessage;
  }

  /**
   * Check if there's a user message after current message
   * User message after current message means user has sent a new request
   * @returns True if there's a user message after current message
   */
  private hasUserMessageAfterCurrent(): boolean {
    if (!this.allMessages || !this.messageId) {
      return false;
    }

    const currentMessageIndex = this.allMessages.findIndex(
      msg => msg.id === this.messageId || msg.messageId === this.messageId
    );

    if (currentMessageIndex === -1) {
      return false;
    }

    // Check if there's any user message after current message
    const aiSenderId = '00000000-0000-0000-0000-000000000001';
    for (let i = currentMessageIndex + 1; i < this.allMessages.length; i++) {
      if (this.allMessages[i].senderId !== aiSenderId) {
        return true; // Found a user message after current message
      }
    }

    return false;
  }

  /**
   * Check if save buttons can be shown
   * Save buttons are hidden when:
   * 1. Revert button is visible
   * 2. User has sent a new message (to prevent confusion during new request)
   */
  showSaveButtons(): boolean {
    // Don't show save buttons if revert button is visible
    if (this.showRevertButton()) {
      return false;
    }

    // Hide save buttons immediately when user sends a new message
    // This prevents confusion and ensures clean UI during new requests
    if (this.hasUserMessageAfterCurrent()) {
      return false;
    }

    return this.showActions &&
           !this.isCompilingLoading &&
           !this.isBundleLoading &&
           !this.getError() &&
           !this.isRetrying &&
           this.getBundleContent() !== '';
  }

  /**
   * Check if "Use this visual element" button should be disabled
   */
  isUseButtonDisabled(): boolean {
    return this.createEntityComponentLoading || !this.activeProjectId || this.isVersionSaved;
  }

  /**
   * Get "Use this visual element" button text
   */
  getUseButtonText(): string {
    if (this.componentMode) {
      if (this.createEntityComponentLoading) {
        return 'Saving as version...';
      }
      if (this.isVersionSaved) {
        return 'Version saved';
      }
      return 'Save as Version';
    }

    if (this.hasDisplayComponent) {
      if (this.createEntityComponentLoading) {
        return 'Saving as version...';
      }
      if (this.isVersionSaved) {
        return 'Version saved';
      }
      return 'Save as Version';
    }

    if (this.createEntityComponentLoading) {
      if (this.isVersionSaved) {
        return 'Setting display component...';
      }
      return 'Saving as version...';
    }
    if (this.isVersionSaved) {
      return 'Visual element is set';
    }
    return 'Use this visual element for entity items';
  }

  /**
   * Get tooltip text for "Use this visual element" button
   */
  getUseButtonTooltip(): string {
    if (this.componentMode) {
      if (this.isVersionSaved) {
        return 'Version has been saved';
      }
      return 'Save this component as a new version';
    }

    if (this.hasDisplayComponent) {
      if (this.isVersionSaved) {
        return 'Version has been saved';
      }
      return 'Save this component as a new version';
    }

    if (this.isVersionSaved) {
      return 'Visual element has been set for entity items';
    }
    return 'Save as version and use this visual element for entity items';
  }

  /**
   * Handle use visual element button click event
   * In update mode: only approves branch (saves as version)
   * In create mode: saves as version, then sets display component
   */
  onUseVisualElementClick(): void {
    if (!this.activeProjectId || !this.responseData || this.createEntityComponentLoading || this.isUseButtonDisabled()) {
      return;
    }

    this.createEntityComponentLoading = true;

    const componentDefinition = this.responseData?.componentDefinition as Record<string, unknown>;
    const componentDefinitionId = componentDefinition?.id as string || this.componentDefinitionId;

    if (!componentDefinitionId) {
      this.createEntityComponentLoading = false;
      return;
    }

    // In update mode, only approve branch (save as version), don't set display component
    if (this.componentMode) {
      if (this.isVersionSaved) {
        this.createEntityComponentLoading = false;
        return;
      }

      const updateDraftId = this.getDraftId();
      if (!updateDraftId) {
        this.createEntityComponentLoading = false;
        return;
      }

      const updateMessageObj = this.message as Record<string, unknown>;
      const updatePageId = (updateMessageObj?.pageId as string) ||
        (this.responseData?.pageId as string) || undefined;
      const updateContentTreeItemId = (updateMessageObj?.contentTreeItemId as string) ||
        (this.responseData?.contentTreeItemId as string) || undefined;
      const updateVersionDescription = (updateMessageObj?.versionDescription as string) ||
        (this.responseData?.versionDescription as string) || undefined;

      const updatePostPlacementTarget = (updatePageId || updateContentTreeItemId) ? {
        pageId: updatePageId,
        contentTreeItemId: updateContentTreeItemId
      } : undefined;

      if (componentDefinitionId) {
        this.aiChatGeneratorService.notifyApproveBranchStarted(componentDefinitionId);
      }

      this.componentGeneratorService.approveBranch(updateDraftId, updatePostPlacementTarget, updateVersionDescription)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: ComponentDefinition | null) => {
            if (response) {
              const responseData = response as Record<string, unknown>;
              if (responseData.version !== undefined) {
                this.componentVersion = responseData.version as number;
              }
              this.isVersionSaved = true;
              this.createEntityComponentLoading = false;

              const approvedComponentDefinitionId = response.id || componentDefinitionId;
              if (approvedComponentDefinitionId) {
                this.aiChatGeneratorService.notifyApproveBranchSuccess(approvedComponentDefinitionId as string);
              }
            } else {
              this.createEntityComponentLoading = false;
              if (componentDefinitionId) {
                this.aiChatGeneratorService.notifyApproveBranchError(componentDefinitionId, 'Approval failed');
              }
            }
          },
          error: (error) => {
            this.createEntityComponentLoading = false;
            if (componentDefinitionId) {
              this.aiChatGeneratorService.notifyApproveBranchError(componentDefinitionId, error);
            }
          }
        });
      return;
    }

    if (!this.projectModelId) {
      this.createEntityComponentLoading = false;
      return;
    }

    const draftId = this.getDraftId();
    if (!draftId) {
      this.createEntityComponentLoading = false;
      return;
    }

    const messageObj = this.message as Record<string, unknown>;
    const pageId = (messageObj?.pageId as string) || (this.responseData?.pageId as string) || undefined;
    const contentTreeItemId = (messageObj?.contentTreeItemId as string) || (this.responseData?.contentTreeItemId as string) || undefined;
    const versionDescription = (messageObj?.versionDescription as string) || (this.responseData?.versionDescription as string) || undefined;

    const postPlacementTarget = (pageId || contentTreeItemId) ? {
      pageId,
      contentTreeItemId
    } : undefined;

    if (componentDefinitionId) {
      this.aiChatGeneratorService.notifyApproveBranchStarted(componentDefinitionId);
    }

    this.componentGeneratorService.approveBranch(draftId, postPlacementTarget, versionDescription)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ComponentDefinition | null) => {
          if (!response) {
            this.createEntityComponentLoading = false;
            if (componentDefinitionId) {
              this.aiChatGeneratorService.notifyApproveBranchError(componentDefinitionId, 'Approval failed');
            }
            return;
          }

          const responseData = response as Record<string, unknown>;
          if (responseData.version !== undefined) {
            this.componentVersion = responseData.version as number;
          }

          const approvedComponentDefinitionId = response.id || componentDefinitionId;
          if (approvedComponentDefinitionId) {
            this.aiChatGeneratorService.notifyApproveBranchSuccess(approvedComponentDefinitionId as string);
          }

          if (!this.projectModelId) {
            this.isVersionSaved = true;
            this.createEntityComponentLoading = false;
            setTimeout(() => this.splitterToggleRequested.emit(true), 100);
            return;
          }

          const projectModelId = this.projectModelId;

          // Check if entity already has a display component
          this.store.select(getProjectModelsWithFields)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((projectModels: ProjectModelWithRelations[] | undefined) => {
              const projectModel = projectModels?.find(pm => pm.id === projectModelId);
              const hasDisplayComponent = this.entityVisualizationService.hasDisplayComponent(projectModel || null);

              if (!hasDisplayComponent) {
                this.aiChatGeneratorService.notifySetDisplayComponentStarted(
                  projectModelId,
                  approvedComponentDefinitionId as string
                );

                this.componentGeneratorService.setDisplayComponent(
                  projectModelId,
                  approvedComponentDefinitionId as string,
                  'flutter'
                )
                  .pipe(takeUntil(this.destroy$))
                  .subscribe({
                    next: () => {
                      this.isVersionSaved = true;
                      this.createEntityComponentLoading = false;
                      this.aiChatGeneratorService.notifySetDisplayComponentSuccess(
                        projectModelId,
                        approvedComponentDefinitionId as string
                      );
                      this.store.dispatch(new ProjectModelActions.GetProjectModels());
                      setTimeout(() => this.splitterToggleRequested.emit(true), 100);
                    },
                    error: (error) => {
                      console.error('Error setting display component:', error);
                      this.isVersionSaved = true;
                      this.createEntityComponentLoading = false;
                      this.aiChatGeneratorService.notifySetDisplayComponentError(projectModelId, error);
                      setTimeout(() => this.splitterToggleRequested.emit(true), 100);
                    }
                  });
              } else {
                this.isVersionSaved = true;
                this.createEntityComponentLoading = false;
                setTimeout(() => this.splitterToggleRequested.emit(true), 100);
              }
            });
        },
        error: (error) => {
          this.createEntityComponentLoading = false;
          if (componentDefinitionId) {
            this.aiChatGeneratorService.notifyApproveBranchError(componentDefinitionId, error);
          }
        }
      });
  }

  /**
   * Get draft ID from response data or message
   */
  getDraftId(): string | null {
    return (this.responseData?.draftId as string) ||
           (this.message as Record<string, unknown>)?.draftId as string ||
           (this.responseData?.branchId as string) ||
           (this.message as Record<string, unknown>)?.branchId as string ||
           null;
  }

  /**
   * Get step ID from response data or message
   */
  getStepId(): string | null {
    return (this.responseData?.stepId as string) ||
           (this.message as Record<string, unknown>)?.stepId as string ||
           null;
  }

  /**
   * Check if component definition mode (update mode)
   */
  get componentMode(): boolean {
    return !!this.componentDefinitionId;
  }

  /**
   * Check if compilation process is complete (regardless of success or error)
   * Compilation is considered complete when loading states are finished
   * @returns True if compilation process has finished
   */
  private isCompilationProcessComplete(): boolean {
    // Compilation is complete when:
    // 1. No compilation loading
    // 2. No bundle loading
    // 3. Not currently retrying
    // Note: We don't check for errors or bundle content - we only care that the process finished
    return !this.isCompilingLoading &&
           !this.isBundleLoading &&
           !this.isRetrying;
  }

  /**
   * Check if AI is currently processing a new message
   * AI is processing if there's a user message after current message without an AI response
   * @returns True if AI is currently thinking/processing
   */
  private isAiProcessingNewMessage(): boolean {
    if (!this.allMessages || !this.messageId) {
      return false;
    }

    const currentMessageIndex = this.allMessages.findIndex(
      msg => msg.id === this.messageId || msg.messageId === this.messageId
    );

    if (currentMessageIndex === -1) {
      return false;
    }

    // Check if there's a user message after current message
    // and no AI response after that user message (meaning AI is thinking)
    const aiSenderId = '00000000-0000-0000-0000-000000000001';

    for (let i = currentMessageIndex + 1; i < this.allMessages.length; i++) {
      const subsequentMessage = this.allMessages[i];

      // If we find a user message (not from AI)
      if (subsequentMessage.senderId !== aiSenderId) {
        // Check if there's an AI response after this user message
        let hasAiResponseAfter = false;
        for (let j = i + 1; j < this.allMessages.length; j++) {
          if (this.allMessages[j].senderId === aiSenderId) {
            hasAiResponseAfter = true;
            break;
          }
        }

        // If there's a user message without an AI response after it, AI is processing
        if (!hasAiResponseAfter) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check if revert button should be shown
   * Revert button is shown for non-final steps after compilation process is complete
   * Hidden when AI is processing a new message to avoid confusion
   * @returns True if revert button should be shown
   */
  showRevertButton(): boolean {
    // Basic validation: must have required data
    if (!this.responseData || !this.messageId || !this.getDraftId() || !this.getStepId()) {
      return false;
    }

    // Don't show revert button if this is the final step
    if (this.isFinalStep()) {
      return false;
    }

    // Don't show revert button if AI is currently processing a new message
    // This prevents showing revert button while "AI is thinking..." is displayed
    if (this.isAiProcessingNewMessage()) {
      return false;
    }

    // Show revert button when compilation process is complete
    // This includes both success and error cases - we only care that the process finished
    return this.isCompilationProcessComplete();
  }

  /**
   * Check if this is the final step in the draft
   * A step is final if there are no subsequent messages (user or AI) in the chat
   * @returns True if this is the final step
   */
  private isFinalStep(): boolean {
    if (!this.allMessages || !this.messageId) {
      return true; // If we can't determine, assume it's final to be safe
    }

    const currentMessageIndex = this.allMessages.findIndex(msg => msg.id === this.messageId || msg.messageId === this.messageId);
    if (currentMessageIndex === -1) {
      return true;
    }

    // If this is the last message in the array, it's the final step
    if (currentMessageIndex === this.allMessages.length - 1) {
      return true;
    }

    // If there are any subsequent messages (user or AI), this is not the final step
    return false;
  }


  /**
   * Handle revert confirmation from popconfirm
   * Reverts the branch to this step and removes all subsequent messages
   */
  onRevertConfirm(): void {
    const draftId = this.getDraftId();
    const stepId = this.getStepId();

    if (!draftId || !stepId || !this.messageId || this.isReverting) {
      return;
    }

    const messageId = this.messageId;
    this.isReverting = true;

    this.componentGeneratorService.revertToStep(draftId, stepId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Emit revert request to parent component to remove subsequent messages
          this.revertRequested.emit({
            messageId,
            draftId,
            stepId
          });
          this.isReverting = false;
        },
        error: (error) => {
          console.error('Error reverting to step:', error);
          this.isReverting = false;
        }
      });
  }

}
